/**
 * Certification renewal — which of an EMT's certifications are approaching expiry, read ONLY from
 * the trusted document checks (profiles/{uid}.certificationChecks, written server-side by
 * verifyCredentialDocument), never from a date the EMT typed.
 *
 * Shared by:
 *  - the institute regional renewal view (functions/src/institutes.ts → listInstituteRenewalLeads),
 *  - institute outreach eligibility (the same function re-checks every recipient at send time),
 *  - the EMT dashboard "Renew your certification" prompt (the EMT's own certifications).
 *
 * Synced verbatim into functions/src/generated/ (scripts/syncFunctionsGenerated.mjs), so the server
 * and the UI agree on the dates, the windows and which institute programs count as a match.
 */
import { resolveCertVerification } from '../credentials/certificationChecks';
import { certificationMatchKey, resolveCanonicalCertification } from '../constants/certifications';
import { haversineMiles } from '../profileCoordinates';
import { scoringClock, scoringToday } from '../readiness/certUtils';

export const RENEWAL_WINDOWS_DAYS = [30, 60, 90] as const;
export type RenewalWindowDays = (typeof RENEWAL_WINDOWS_DAYS)[number];
export const MAX_RENEWAL_WINDOW_DAYS: RenewalWindowDays = 90;

/** Radius choices for "city plus radius" (miles). The server refuses anything larger than the last. */
export const RENEWAL_RADIUS_OPTIONS_MILES = [25, 50, 100, 150] as const;
export const MAX_RENEWAL_RADIUS_MILES = 150;

/**
 * A check result's extracted expiration date is trusted only when the document passed the automated
 * checks (name, date of birth, not expired, genuine credential). A document rejected for a name
 * mismatch may not even belong to this EMT, so its date is not used.
 */
const TRUSTED_EXPIRY_STATUSES = new Set(['under_review', 'verified']);

const DAY_MS = 86_400_000;

export type TrustedCertExpiry = {
  /** Certification name as the EMT entered it (trimmed). */
  certName: string;
  /** Canonical name when it is a known certification (BLS, ACLS, NREMT-B …), else null. */
  canonical: string | null;
  /** YYYY-MM-DD, from the document itself. */
  expirationDate: string;
  /** Whole days from the EMT's own day (credentialDayFor) to the expiration date; negative once expired. */
  daysUntil: number;
  /** The trusted check status the date came from. */
  status: string;
};

/** Parses the extracted date: "YYYY-MM-DD" (what the checker writes) or "MM/DD/YYYY". UTC midnight ms. */
export function parseExpiryDate(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  let m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
  if (m) return Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]));
  return null;
}

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * THE day an EMT's credentials are counted on (2026-10-13 logic audit): the EMT's own calendar day, in
 * the time zone of their address state, else UTC (certUtils.scoringClock / scoringToday, the same day
 * readiness scores use). YYYY-MM-DD. Used by the EMT's screens, the reminder jobs, the document
 * check's "expired" test, institute renewal windows and the employer team views (where it is each
 * MEMBER's day, never the viewer's browser day and never the server's UTC day).
 */
export function credentialDayFor(profile: unknown, nowMs: number = Date.now()): string {
  return scoringToday(scoringClock(profile, nowMs));
}

/**
 * One entry per certification (alias-aware: NREMT-B ≡ EMT-B), carrying the LATEST trusted expiration
 * among that certification's documents — an EMT who already uploaded their renewed card is not
 * "expiring" because the old card is still on the profile.
 *
 * `includeRejectedExpired`: for the EMT's own view only — a card the check rejected because it is
 * already expired is still the EMT's real card, and "expired, renew it" is the most useful prompt.
 */
export function trustedCertExpiries(
  profile: unknown,
  now: number = Date.now(),
  opts: {
    includeRejectedExpired?: boolean;
    /**
     * The calendar day to count from, YYYY-MM-DD. Defaults to the EMT's own day at `now`
     * (credentialDayFor: their address state's zone, else UTC), so the dashboard, the renewal
     * banner, the reminder jobs and institute renewal leads count the same number of days. Before
     * 2026-10-13 the default was the server's UTC day.
     */
    today?: string;
  } = {},
): TrustedCertExpiry[] {
  const p = (profile ?? {}) as { professional?: { certifications?: unknown }; certificationChecks?: unknown };
  const certs = Array.isArray(p.professional?.certifications) ? (p.professional?.certifications as unknown[]) : [];
  const byKey = new Map<string, TrustedCertExpiry>();
  const today = parseExpiryDate(opts.today) ?? (parseExpiryDate(credentialDayFor(profile, now)) as number);

  for (const cert of certs) {
    if (!cert || typeof cert !== 'object') continue;
    const name = String((cert as { name?: unknown }).name ?? '').trim();
    if (!name) continue;
    const check = resolveCertVerification(cert, p.certificationChecks);
    if (!check) continue;
    const trusted =
      TRUSTED_EXPIRY_STATUSES.has(check.status) ||
      (opts.includeRejectedExpired === true && check.status === 'rejected' && check.reasonCode === 'expired');
    if (!trusted) continue;
    const expMs = parseExpiryDate(check.extracted?.expirationDate);
    if (expMs === null) continue;
    const key = certificationMatchKey(name);
    const entry: TrustedCertExpiry = {
      certName: name,
      canonical: resolveCanonicalCertification(name),
      expirationDate: isoDay(expMs),
      daysUntil: Math.round((expMs - today) / DAY_MS),
      status: check.status,
    };
    const prev = byKey.get(key);
    if (!prev || entry.expirationDate > prev.expirationDate) byKey.set(key, entry);
  }
  return [...byKey.values()].sort((a, b) => a.daysUntil - b.daysUntil);
}

/** Smallest window (30/60/90) that contains `daysUntil`; null when already expired or further out. */
export function renewalWindowFor(daysUntil: number): RenewalWindowDays | null {
  if (!Number.isFinite(daysUntil) || daysUntil < 0) return null;
  for (const w of RENEWAL_WINDOWS_DAYS) if (daysUntil <= w) return w;
  return null;
}

export function renewalWindowLabel(window: RenewalWindowDays): string {
  if (window === 30) return 'Expires within 30 days';
  if (window === 60) return 'Expires in 31–60 days';
  return 'Expires in 61–90 days';
}

/** True when one of the institute's programs trains for `certName` (alias-aware, same rule as job matching). */
export function instituteOffersCertification(programs: unknown, certName: string): boolean {
  if (!Array.isArray(programs) || !certName.trim()) return false;
  const key = certificationMatchKey(certName);
  return programs.some((p) => typeof p === 'string' && p.trim() && certificationMatchKey(p) === key);
}

export type RenewalRegion =
  | { kind: 'state'; state: string }
  | { kind: 'radius'; center: { lat: number; lng: number }; radiusMiles: number; label?: string };

/** Validates and normalizes a region sent by a client. Returns null when it is unusable. */
export function normalizeRenewalRegion(raw: unknown): RenewalRegion | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (r.kind === 'state') {
    const state = String(r.state ?? '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(state) ? { kind: 'state', state } : null;
  }
  if (r.kind === 'radius') {
    const c = (r.center ?? {}) as { lat?: unknown; lng?: unknown };
    const lat = Number(c.lat);
    const lng = Number(c.lng);
    const radiusMiles = Number(r.radiusMiles);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    if (!Number.isFinite(radiusMiles) || radiusMiles <= 0 || radiusMiles > MAX_RENEWAL_RADIUS_MILES) return null;
    const label = typeof r.label === 'string' ? r.label.slice(0, 80) : undefined;
    return { kind: 'radius', center: { lat, lng }, radiusMiles, ...(label ? { label } : {}) };
  }
  return null;
}

/** Whether an EMT's (city-level) location falls in the region. No coordinates → not in a radius region. */
export function emtInRenewalRegion(
  emt: { state?: string | null; coords?: { lat: number; lng: number } | null },
  region: RenewalRegion,
): boolean {
  if (region.kind === 'state') return (emt.state ?? '').trim().toUpperCase() === region.state;
  if (!emt.coords) return false;
  return haversineMiles(region.center.lat, region.center.lng, emt.coords.lat, emt.coords.lng) <= region.radiusMiles;
}

export function isRenewalWindow(value: unknown): value is RenewalWindowDays {
  return (RENEWAL_WINDOWS_DAYS as readonly number[]).includes(Number(value));
}
